/**
 * Onboarding: three steps, shown once after first sign-in, reopenable from
 * Help forever after.
 *
 * Finishing writes the completion server-side and refreshes the session so
 * the guard stops routing here. Reopening from Help renders the same steps
 * without rewriting anything — completion is a fact with one timestamp, not
 * a counter.
 */

import { useCallback, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { completeOnboarding } from '../api.js';
import { NEXT_STORAGE_KEY, safeNextPath } from './SignInPage.js';
import { useWorkspaceCopy } from '../components.js';
import { useSession } from '../session.js';
import { Button, InlineAlert, useToasts } from '../ui.js';

export function OnboardingPage(): ReactNode {
  const copy = useWorkspaceCopy();
  const { session, refresh } = useSession();
  const navigate = useNavigate();
  const { announce } = useToasts();
  const [step, setStep] = useState(0);
  const [finishing, setFinishing] = useState(false);
  const [failed, setFailed] = useState(false);

  const steps = copy.onboarding.steps;
  const total = steps.length;
  const current = steps[step] ?? { title: '', body: '' };
  const alreadyDone =
    session.status === 'authenticated' && session.profile.onboardingCompleted;

  const finish = useCallback(() => {
    setFinishing(true);
    setFailed(false);
    completeOnboarding()
      .then(async () => {
        await refresh();
        announce(copy.toasts.onboardingDone);
        // The destination preserved through sign-in (or through the guard's
        // redirect here) is consumed now — onboarding was a detour on the
        // way somewhere, and this is where the journey resumes. Only a safe
        // same-origin path ever comes back from `safeNextPath`.
        let next: string | null = null;
        try {
          next = safeNextPath(sessionStorage.getItem(NEXT_STORAGE_KEY));
          sessionStorage.removeItem(NEXT_STORAGE_KEY);
        } catch {
          // Without storage the dashboard is the destination, as before.
        }
        await navigate(next ?? '/dashboard', { replace: true });
      })
      .catch(() => {
        setFinishing(false);
        setFailed(true);
      });
  }, [refresh, navigate, announce, copy]);

  return (
    <div
      style={{
        minHeight: alreadyDone ? undefined : '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--surface-page)',
        padding: 'var(--space-lg)',
      }}
    >
      <section
        aria-labelledby="onboarding-title"
        style={{
          width: 'min(560px, 100%)',
          background: 'var(--surface-card)',
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-panel)',
          boxShadow: 'var(--elevation-1)',
          padding: 'var(--space-xl)',
        }}
      >
        <p
          style={{
            font: 'var(--type-micro-cap)',
            letterSpacing: 'var(--type-micro-cap-tracking)',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            marginBottom: 8,
          }}
        >
          {copy.onboarding.stepLabel(step + 1, total)}
        </p>

        <h1
          id="onboarding-title"
          style={{
            font: 'var(--type-display-md)',
            letterSpacing: 'var(--type-display-md-tracking)',
            color: 'var(--text-primary)',
            marginBottom: 6,
          }}
        >
          {step === 0 ? copy.onboarding.title : current.title}
        </h1>
        {step === 0 ? (
          <h2
            style={{
              font: 'var(--type-heading-sm)',
              color: 'var(--text-secondary)',
              marginBottom: 10,
            }}
          >
            {current.title}
          </h2>
        ) : null}

        <p
          style={{
            font: 'var(--type-body-md)',
            color: 'var(--text-secondary)',
            lineHeight: 1.8,
            marginBottom: 'var(--space-lg)',
          }}
        >
          {current.body}
        </p>

        {/* Progress: dots plus the text above — never colour alone. */}
        <div
          aria-hidden="true"
          style={{ display: 'flex', gap: 6, marginBottom: 'var(--space-lg)' }}
        >
          {steps.map((entry, index) => (
            <span
              key={entry.title}
              style={{
                width: index === step ? 22 : 8,
                height: 8,
                borderRadius: 'var(--radius-pill)',
                background:
                  index <= step ? 'var(--action-primary)' : 'var(--border-strong)',
                transition: 'var(--transition-control)',
              }}
            />
          ))}
        </div>

        {failed ? (
          <InlineAlert tone="error" title={copy.states.saveFailedTitle} role="alert">
            {copy.states.saveFailedBody}
          </InlineAlert>
        ) : null}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="quiet"
            onClick={() => {
              setStep((value) => Math.max(0, value - 1));
            }}
            disabled={step === 0 || finishing}
          >
            {copy.onboarding.back}
          </Button>

          {step < total - 1 ? (
            <Button
              variant="primary"
              onClick={() => {
                setStep((value) => Math.min(total - 1, value + 1));
              }}
            >
              {copy.onboarding.next}
            </Button>
          ) : alreadyDone ? (
            <Button
              variant="primary"
              onClick={() => {
                void navigate('/dashboard');
              }}
            >
              {copy.onboarding.finish}
            </Button>
          ) : (
            <Button variant="primary" onClick={finish} busy={finishing}>
              {copy.onboarding.finish}
            </Button>
          )}
        </div>

        <p
          style={{
            font: 'var(--type-caption)',
            color: 'var(--text-muted)',
            marginTop: 'var(--space-md)',
          }}
        >
          {copy.onboarding.skipNote}
        </p>
      </section>
    </div>
  );
}
